import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { ExternalLink, LinkIcon } from "lucide-react";

// Renders a rich inline preview card for a URL (Open Graph metadata).
// If the post already includes a `meta` prop (server-cached at write-time), uses it directly.
// Otherwise fetches /api/link-preview on mount.
const LinkPreviewCard = ({ url, meta, compact = false }) => {
  const [data, setData] = useState(meta || null);
  const [loading, setLoading] = useState(!meta);

  useEffect(() => {
    if (meta || !url) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/link-preview`, { params: { url } });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ url, title: null, description: null, image: null, site_name: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [url, meta]);

  if (!url) return null;

  let hostname = "";
  try { hostname = new URL(url).hostname.replace(/^www\./, ""); } catch { /* invalid URL */ }

  // Minimal fallback while loading or when no rich metadata available
  if (loading) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="block mt-3 rounded-xl border border-[#1A2030] bg-[#050810] p-3 hover:border-[#EC008C]/40 transition-colors" data-testid="link-preview-loading">
        <div className="h-12 animate-pulse bg-[#1A2030] rounded" />
      </a>
    );
  }

  const hasRich = data?.image || (data?.title && data?.title.length > 3);
  if (!hasRich) {
    // Plain compact link chip
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
         className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full bg-[#050810] border border-[#1A2030] hover:border-[#EC008C]/40 transition-colors text-xs text-[#EC008C]"
         data-testid="link-preview-bare">
        <LinkIcon className="w-3 h-3" /> {hostname || url}
        <ExternalLink className="w-3 h-3" />
      </a>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
       className="block mt-3 rounded-xl border border-[#1A2030] bg-[#050810] overflow-hidden hover:border-[#EC008C]/40 transition-colors group"
       data-testid="link-preview-card">
      <div className={`flex ${compact ? "flex-row" : "flex-col sm:flex-row"} gap-0`}>
        {data.image && (
          <div className={`${compact ? "w-24 h-24" : "w-full sm:w-44 h-44 sm:h-32"} flex-shrink-0 bg-[#1A2030] overflow-hidden`}>
            <img
              src={data.image}
              alt={data.title || hostname}
              className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
        )}
        <div className="flex-1 p-3 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-[#EC008C] mb-1 flex items-center gap-1">
            <LinkIcon className="w-3 h-3" />
            {data.site_name || hostname}
          </p>
          {data.title && (
            <p className="text-sm font-semibold text-white line-clamp-2 mb-1">{data.title}</p>
          )}
          {!compact && data.description && (
            <p className="text-xs text-[#8A92A0] line-clamp-2">{data.description}</p>
          )}
        </div>
      </div>
    </a>
  );
};

export default LinkPreviewCard;
